/**
 * ScenarioSelector Component
 * Grid of simulation scenarios with preview, scenario picker and start buttons
 */

import { useEffect, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronRight, Play, RefreshCw } from 'lucide-react';
import { scenarioTypes } from './constants';
import { ScenarioVariantPicker } from './ScenarioVariantPicker';

const ScenarioPreview = ({ api, scenario, loading, selectedVariantId, setSelectedVariantId, startSimulation, onlyVariantId }) => {
    const ref = useRef(null);
    useEffect(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [scenario.id]);

    return (
        <Card ref={ref} className={`border-2 ${scenario.borderColor} animate-fade-in scroll-mt-20`} data-testid="scenario-preview">
            <CardContent className="p-6">
                <div className="flex items-start gap-4">
                    <div className={`hidden sm:block p-4 rounded-xl ${scenario.bgColor}`}>
                        <scenario.icon className={`h-8 w-8 ${scenario.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-semibold">{scenario.title}</h3>
                        <p className="text-muted-foreground mt-2">{scenario.description}</p>
                        <div className="flex flex-wrap gap-2 mt-4">
                            {scenario.skills.map((skill) => (
                                <Badge key={skill} variant="secondary">{skill}</Badge>
                            ))}
                        </div>
                        <ScenarioVariantPicker
                            api={api}
                            scenarioType={scenario.id}
                            selectedVariantId={selectedVariantId}
                            onSelect={setSelectedVariantId}
                            onSurprise={() => startSimulation(scenario.id, null)}
                            loading={loading}
                            onlyVariantId={onlyVariantId}
                        />
                        <Button
                            onClick={() => startSimulation(scenario.id, selectedVariantId)}
                            className="mt-4 btn-primary"
                            disabled={loading || !selectedVariantId}
                            data-testid="start-simulation-btn"
                        >
                            {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                            {selectedVariantId ? 'Start Selected Scenario' : 'Select a scenario to start'}
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export const ScenarioSelector = ({
    api,
    selectedScenario,
    setSelectedScenario,
    selectedVariantId,
    setSelectedVariantId,
    loadingScenario,
    startSimulation,
    assignedVariantId,
    assignedOnly
}) => {
    return (
        <div className="space-y-6">
            {selectedScenario && !selectedScenario.isJobAnalysis && !selectedScenario.isCommunication && (
                <ScenarioPreview
                    api={api}
                    scenario={selectedScenario}
                    loading={loadingScenario === selectedScenario.id}
                    selectedVariantId={selectedVariantId}
                    setSelectedVariantId={setSelectedVariantId}
                    startSimulation={startSimulation}
                    onlyVariantId={assignedVariantId}
                />
            )}

            {!assignedOnly && <div className="grid gap-4 md:grid-cols-2">
                {scenarioTypes.map((scenario) => {
                    const Icon = scenario.icon;
                    const isSelected = selectedScenario?.id === scenario.id;
                    return (
                        <Card 
                            key={scenario.id} 
                            className={`card-hover cursor-pointer transition-all duration-200 ${
                                isSelected ? `ring-2 ring-offset-2 ${scenario.borderColor} ring-current` : ''
                            }`}
                            onClick={() => setSelectedScenario(scenario)}
                            data-testid={`scenario-${scenario.id}`}
                        >
                            <CardContent className="p-6">
                                <div className="flex items-start gap-4">
                                    <div className={`p-3 rounded-xl ${scenario.bgColor}`}>
                                        <Icon className={`h-6 w-6 ${scenario.color}`} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-lg">{scenario.title}</h3>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            {scenario.description}
                                        </p>
                                        {scenario.scenarioCount && (
                                            <p className="text-xs font-medium text-primary mt-2">{scenario.scenarioCount}</p>
                                        )}
                                    </div>
                                    <ChevronRight className={`h-5 w-5 transition-colors ${
                                        isSelected ? scenario.color : 'text-muted-foreground'
                                    }`} />
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>}
        </div>
    );
};
